import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Award, RotateCcw, Sparkles, Trophy, Users } from "lucide-react";
import { getStudentId } from "@/lib/api";
import { clearActiveSessionCode, getStudyState } from "@/lib/studyApi";

export default function StudyComplete() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    clearActiveSessionCode();
    getStudyState(code, getStudentId()).then(({ data }) => setState(data)).catch(() => navigate("/"));
  }, [code]);

  if (!state) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  const summary = state.session.summary || { group_observations: [], students: {} };
  const totalCorrect = state.participants.reduce((a, p) => a + p.correct_count, 0);
  const totalAttempts = state.participants.reduce((a, p) => a + p.attempt_count, 0);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10" data-testid="study-complete-page">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-success/15 flex items-center justify-center mx-auto mb-3">
            <Trophy className="text-success" size={26} />
          </div>
          <h1 className="font-heading font-extrabold text-2xl">Session Complete!</h1>
          <p className="text-sm text-muted-foreground">{state.session.topic} · {state.session.subject}</p>
        </div>

        <div className="bg-card border border-border rounded-3xl p-6">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5"><Users size={13} /> Group Performance</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-muted rounded-xl p-3 text-center"><p className="font-heading font-extrabold text-xl">{totalAttempts ? Math.round((totalCorrect / totalAttempts) * 100) : 0}%</p><p className="text-[10px] text-muted-foreground">Group Accuracy</p></div>
            <div className="bg-muted rounded-xl p-3 text-center"><p className="font-heading font-extrabold text-xl">{totalCorrect}/{totalAttempts}</p><p className="text-[10px] text-muted-foreground">Correct Answers</p></div>
          </div>
          {summary.group_observations?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-bold text-muted-foreground">SmartGuru Observations</p>
              {summary.group_observations.map((o, i) => (
                <p key={i} className="text-sm text-muted-foreground flex gap-2"><Sparkles size={13} className="text-primary shrink-0 mt-0.5" /> {o}</p>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-3xl p-6 space-y-3" data-testid="study-complete-individual-progress">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Award size={13} /> Individual Progress</p>
          {state.participants.map((p) => {
            const s = summary.students?.[p.display_name] || {};
            return (
              <div key={p.student_id} className="bg-muted rounded-xl p-3" data-testid={`individual-progress-${p.student_id}`}>
                <div className="flex justify-between items-center mb-1">
                  <p className="font-bold text-sm">{p.display_name}</p>
                  <p className="text-sm font-heading font-bold text-primary">{Math.round(p.mastery_score || s.mastery || 0)}%</p>
                </div>
                {s.next_step && <p className="text-xs text-muted-foreground">{s.next_step}</p>}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => navigate("/")} data-testid="continue-individually-button" className="flex-1 h-12 rounded-xl border-2 border-border font-bold text-sm hover:bg-muted">Continue Individually</button>
          <button onClick={() => navigate("/study/create")} data-testid="study-together-again-button" className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary/90">
            <RotateCcw size={15} /> Combine Study Again
          </button>
        </div>
      </div>
    </div>
  );
}
